package co.maytes.standupbot.dashboard.lib.api

import co.maytes.standupbot.dashboard.lib.auth.TokenStore
import co.maytes.standupbot.shared.dto.common.ProblemDetailsDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.DefaultRequest
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.content.TextContent
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

class ApiClient(
    private val baseUrl: String,
) {
    @PublishedApi
    internal val json: Json = Json {
        ignoreUnknownKeys = true
        coerceInputValues = true
        encodeDefaults = true
    }

    val http: HttpClient = HttpClient(Js) {
        install(ContentNegotiation) { json(json) }
        install(HttpTimeout) {
            requestTimeoutMillis = 30_000
        }
        install(DefaultRequest) {
            contentType(ContentType.Application.Json)
            if (baseUrl.isNotBlank()) {
                url(baseUrl)
            }
        }
        expectSuccess = false
    }

    @PublishedApi
    internal var tokenStore: TokenStore? = null

    fun wire(tokenStore: TokenStore) {
        this.tokenStore = tokenStore
    }

    suspend inline fun <reified T> get(
        path: String,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<T, ApiError> = sendNoBody(HttpMethod.Get, path, build)

    suspend inline fun <reified T> post(
        path: String,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<T, ApiError> = sendNoBody(HttpMethod.Post, path, build)

    suspend inline fun <reified T> delete(
        path: String,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<T, ApiError> = sendNoBody(HttpMethod.Delete, path, build)

    suspend inline fun <reified Body : Any, reified Resp> post(
        path: String,
        body: Body,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<Resp, ApiError> = submit(HttpMethod.Post, path, body, build)

    suspend inline fun <reified Body : Any, reified Resp> put(
        path: String,
        body: Body,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<Resp, ApiError> = submit(HttpMethod.Put, path, body, build)

    suspend inline fun <reified Body : Any, reified Resp> patch(
        path: String,
        body: Body,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<Resp, ApiError> = submit(HttpMethod.Patch, path, body, build)

    suspend inline fun <reified Body : Any, reified Resp> submit(
        method: HttpMethod,
        path: String,
        body: Body,
        noinline build: HttpRequestBuilder.() -> Unit = {},
    ): Result<Resp, ApiError> = try {
        val payload = TextContent(json.encodeToString(body), ContentType.Application.Json)
        val response = http.request(path) {
            this.method = method
            tokenStore?.accessToken?.let { bearerAuth(it) }
            setBody(payload)
            build()
        }
        when {
            response.status.value in 200..299 -> Result.Ok(response.body<Resp>())
            response.status.value == 401 && tokenStore?.tryRefresh() == true -> {
                val retry = http.request(path) {
                    this.method = method
                    tokenStore?.accessToken?.let { bearerAuth(it) }
                    setBody(payload)
                    build()
                }
                if (retry.status.value in 200..299) Result.Ok(retry.body<Resp>())
                else Result.Err(retry.toApiError(json))
            }
            response.status.value == 401 -> Result.Err(ApiError.Unauthorized)
            else -> Result.Err(response.toApiError(json))
        }
    } catch (t: Throwable) {
        Result.Err(ApiError.Network(t.message ?: "unknown"))
    }

    suspend inline fun <reified T> sendNoBody(
        method: HttpMethod,
        path: String,
        noinline build: HttpRequestBuilder.() -> Unit,
    ): Result<T, ApiError> = try {
        val response = http.request(path) {
            this.method = method
            tokenStore?.accessToken?.let { bearerAuth(it) }
            build()
        }
        when {
            response.status.value in 200..299 -> Result.Ok(response.body<T>())
            response.status.value == 401 && tokenStore?.tryRefresh() == true -> {
                val retry = http.request(path) {
                    this.method = method
                    tokenStore?.accessToken?.let { bearerAuth(it) }
                    build()
                }
                if (retry.status.value in 200..299) Result.Ok(retry.body<T>())
                else Result.Err(retry.toApiError(json))
            }
            response.status.value == 401 -> Result.Err(ApiError.Unauthorized)
            else -> Result.Err(response.toApiError(json))
        }
    } catch (t: Throwable) {
        Result.Err(ApiError.Network(t.message ?: "unknown"))
    }
}

suspend fun HttpResponse.toApiError(json: Json): ApiError = try {
    val text = body<String>()
    if (text.isNotBlank()) {
        val details = json.decodeFromString(ProblemDetailsDto.serializer(), text)
        ApiError.Http(
            status = status.value,
            title = details.title,
            detail = details.detail,
            traceId = details.traceId,
            type = details.type,
        )
    } else {
        ApiError.Http(
            status = status.value,
            title = status.description,
            detail = null,
            traceId = null,
        )
    }
} catch (t: Throwable) {
    ApiError.Http(
        status = status.value,
        title = status.description,
        detail = null,
        traceId = null,
    )
}
